import React, { useEffect, useState } from 'react';
import { API_URL, apiFetch } from '../config/api';
import CommandTransportBadge from './CommandTransportBadge';
import { useNotification } from '../contexts/NotificationContext';

/**
 * Flight Control Panel - Stitch style consolidated flight controls.
 * Mode select, arm/disarm, force arm/disarm, takeoff, land/RTL, emergency stop.
 */
function FlightControlPanel({ telemetry = {}, droneId = null, onActionComplete = null }) {
    const [takeoffAlt, setTakeoffAlt] = useState(10);
    const [selectedMode, setSelectedMode] = useState('GUIDED');
    const [loading, setLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [uiTelemetry, setUiTelemetry] = useState(telemetry || {});
    const [cmdRefresh, setCmdRefresh] = useState(0);
    const { showConfirm } = useNotification();

    useEffect(() => {
        setUiTelemetry(telemetry || {});
    }, [telemetry]);

    const connected = uiTelemetry.connected || false;
    const armed = uiTelemetry.armed || false;
    const mode = uiTelemetry.mode || 'N/A';

    const applyOptimisticState = (endpoint, body = {}) => {
        setUiTelemetry(prev => {
            const next = { ...prev };
            if (endpoint === '/arm') {
                next.armed = true;
                if (!next.mode || next.mode === 'N/A') next.mode = 'GUIDED';
            }
            if (endpoint === '/disarm' || endpoint === '/force-disarm') next.armed = false;
            if (endpoint === '/takeoff') { next.armed = true; next.mode = 'GUIDED'; }
            if (endpoint === '/land') next.mode = 'LAND';
            if (endpoint === '/mode' && body.mode) next.mode = body.mode;
            return next;
        });
    };

    const sendCommand = async (endpoint, body = {}) => {
        if (!droneId) {
            setStatusMessage('❌ Please select a drone first');
            return;
        }
        setLoading(true);
        setStatusMessage('Sending command...');
        try {
            const res = await apiFetch(`${API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ droneId, ...body })
            });
            const data = await res.json();
            if (data.success) {
                applyOptimisticState(endpoint, body);
                if (onActionComplete) await onActionComplete({ endpoint, body, response: data });
                setStatusMessage(`✅ ${data.message || 'Success'}`);
            } else {
                setStatusMessage(`❌ ${data.message || 'Failed'}`);
            }
            return data;
        } catch (error) {
            setStatusMessage(`❌ Error: ${error.message}`);
            return null;
        } finally {
            setLoading(false);
            setCmdRefresh((n) => n + 1);
            setTimeout(() => setStatusMessage(''), 3000);
        }
    };

    // Overrides the autopilot's pre-arm safety checks — the operator confirms.
    const forceArmDrone = async () => {
        if (await showConfirm("FORCE ARM skips the autopilot's pre-arm safety checks (compass, EKF, battery, fence). Arm anyway?")) {
            sendCommand('/arm', { force_arm: true });
        }
    };
    // Cuts the motors: a flying drone falls. FORCE DISARM and EMERGENCY STOP
    // used to act on a single click (the planner already confirmed).
    const forceDisarmDrone = async () => {
        if (await showConfirm('This cuts all motors immediately. If the drone is flying it WILL fall. Continue?')) {
            sendCommand('/force-disarm');
        }
    };
    // LAND is refused while a mission runs; landing anyway is confirmed.
    const landNow = async () => {
        const data = await sendCommand('/land');
        if (data && !data.success && /mission is running/i.test(data.message || '')
            && await showConfirm('A mission is running. Land where the drone is now, leaving its route?')) {
            await sendCommand('/land', { force: true });
        }
    };

    if (!droneId) {
        return (
            <div className="panel-border" style={{
                background: 'var(--stitch-surface-lowest)',
                borderRadius: '12px',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--textMuted)',
                fontSize: '13px',
                fontStyle: 'italic',
            }}>
                Select a drone to access controls
            </div>
        );
    }

    const icon = (name, size = 22) => (
        <span className="material-symbols-outlined" style={{ fontSize: `${size}px` }}>{name}</span>
    );
    const disabledAll = !connected || loading;
    // LAND, RTL and the motor cut stay usable while another request is pending:
    // over HTTP a takeoff request stays open for the whole climb.
    const disabledSafety = !connected;

    return (
        <div className="panel-border custom-scrollbar" style={{
            background: 'var(--stitch-surface-lowest)',
            borderRadius: '12px',
            padding: '20px',
            height: '100%',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
        }}>
            {/* Header */}
            <div>
                <h3 style={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: 'var(--accentSecondary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    {icon('gamepad', 24)}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>CONTROLS — {droneId}</span>
                </h3>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
                    <div style={{ fontSize: '13px', color: 'var(--textSecondary)' }}>
                        Current Mode: <strong style={{ color: 'var(--textPrimary)' }}>{mode}</strong>
                    </div>
                    <CommandTransportBadge droneId={droneId} refreshSignal={cmdRefresh} />
                </div>
            </div>

            {!connected && (
                <div style={{
                    padding: '10px 12px',
                    background: 'rgba(239, 68, 68, 0.1)',
                    border: '1px solid var(--danger)',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: 'var(--danger)',
                    fontWeight: 600,
                }}>
                    ⚠️ Drone is offline. Controls unlock once it is online.
                </div>
            )}

            {/* Flight Mode */}
            <div>
                <p style={{ fontFamily: 'var(--font-label)', fontSize: '12px', color: 'var(--textSecondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                    Flight Mode
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <select
                        value={selectedMode}
                        onChange={(e) => setSelectedMode(e.target.value)}
                        disabled={disabledAll}
                        style={{
                            flex: 1,
                            minWidth: 0,
                            background: 'var(--bgTertiary)',
                            border: '1px solid var(--borderSecondary)',
                            color: 'var(--textPrimary)',
                            fontSize: '14px',
                            fontWeight: 600,
                            borderRadius: '8px',
                            padding: '0 12px',
                            height: '40px',
                            cursor: connected ? 'pointer' : 'not-allowed',
                        }}
                    >
                        <option value="GUIDED">GUIDED</option>
                        <option value="AUTO">AUTO</option>
                        <option value="LOITER">LOITER</option>
                        <option value="ALT_HOLD">ALT_HOLD</option>
                        <option value="RTL">RTL</option>
                        <option value="STABILIZE">STABILIZE</option>
                        <option value="LAND">LAND</option>
                    </select>
                    <button
                        onClick={() => sendCommand('/mode', { mode: selectedMode })}
                        disabled={disabledAll}
                        style={{
                            flex: 1,
                            width: 'auto',
                            margin: 0,
                            padding: '0 14px',
                            height: '40px',
                            background: disabledAll ? 'var(--bgTertiary)' : 'var(--accentSecondary)',
                            color: disabledAll ? 'var(--textMuted)' : 'var(--stitch-on-primary)',
                            fontWeight: 700,
                            fontSize: '13px',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: disabledAll ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        SET MODE
                    </button>
                </div>
            </div>

            {/* Arming controls */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <BigBtn onClick={() => sendCommand('/arm')} disabled={disabledAll || armed}
                    bg="#e28a1c" color="#1a1c1e" iconEl={icon('lock_open', 18)}>ARM</BigBtn>
                <BigBtn onClick={() => sendCommand('/disarm')} disabled={disabledAll || !armed}
                    bg="var(--surface-container-highest, #343536)" color="var(--textPrimary)" border="var(--borderSecondary)" iconEl={icon('lock', 18)}>DISARM</BigBtn>
                <OutlineBtn onClick={forceArmDrone} disabled={disabledAll || armed} color="#e28a1c">FORCE ARM</OutlineBtn>
                <OutlineBtn onClick={forceDisarmDrone} disabled={disabledSafety} color="var(--danger)">FORCE DISARM</OutlineBtn>
            </div>

            {/* Takeoff */}
            <div>
                <p style={{ fontFamily: 'var(--font-label)', fontSize: '12px', color: 'var(--textSecondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '10px' }}>
                    Takeoff Altitude
                </p>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bgTertiary)', border: '1px solid var(--borderSecondary)', borderRadius: '8px', padding: '0 12px', height: '40px', flexShrink: 0 }}>
                        <input
                            type="number" min="1" max="100"
                            value={takeoffAlt}
                            onChange={(e) => setTakeoffAlt(parseInt(e.target.value) || 10)}
                            disabled={disabledAll}
                            style={{
                                width: '46px',
                                background: 'transparent',
                                border: 'none',
                                borderBottom: '2px solid var(--accent)',
                                color: 'var(--textPrimary)',
                                fontSize: '16px',
                                fontWeight: 600,
                                textAlign: 'center',
                                padding: '2px 0',
                                outline: 'none',
                            }}
                        />
                        <span style={{ fontFamily: 'var(--font-label)', fontSize: '11px', color: 'var(--textSecondary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>M</span>
                    </div>
                    <button
                        onClick={() => sendCommand('/takeoff', { altitude: takeoffAlt })}
                        disabled={disabledAll || !armed}
                        style={{
                            flex: 1,
                            margin: 0,
                            height: '40px',
                            background: (disabledAll || !armed) ? 'var(--bgTertiary)' : 'var(--accentSecondary)',
                            color: (disabledAll || !armed) ? 'var(--textMuted)' : 'var(--stitch-on-primary)',
                            fontWeight: 700,
                            fontSize: '13px',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: (disabledAll || !armed) ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                        }}
                    >
                        {icon('flight_takeoff', 18)} INITIATE TAKEOFF
                    </button>
                </div>
            </div>

            {/* Land / RTL */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <BigBtn onClick={landNow} disabled={disabledSafety || !armed}
                    bg="var(--bgTertiary)" color="var(--textPrimary)" border="var(--borderSecondary)" iconEl={icon('flight_land', 18)}>LAND</BigBtn>
                <BigBtn onClick={() => sendCommand('/mode', { mode: 'RTL' })} disabled={disabledSafety || !armed}
                    bg="var(--bgTertiary)" color="var(--textPrimary)" border="var(--borderSecondary)" iconEl={icon('home_pin', 18)}>RTL</BigBtn>
            </div>

            {/* Emergency */}
            <button
                onClick={forceDisarmDrone}
                disabled={disabledSafety}
                style={{
                    width: '100%',
                    margin: 0,
                    padding: '10px',
                    background: disabledSafety ? 'var(--bgTertiary)' : 'var(--danger)',
                    color: disabledSafety ? 'var(--textMuted)' : '#3b0000',
                    fontWeight: 800,
                    fontSize: '13px',
                    border: '2px solid rgba(255,255,255,0.2)',
                    borderRadius: '8px',
                    cursor: disabledSafety ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    boxShadow: disabledSafety ? 'none' : '0 0 30px rgba(239,68,68,0.35)',
                }}
            >
                {icon('emergency_home', 20)} EMERGENCY STOP
            </button>

            {statusMessage && (
                <div style={{
                    padding: '10px 12px',
                    background: statusMessage.includes('✅') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    border: `1px solid ${statusMessage.includes('✅') ? 'var(--success)' : 'var(--danger)'}`,
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: statusMessage.includes('✅') ? 'var(--success)' : 'var(--danger)',
                    fontWeight: 600,
                }}>
                    {statusMessage}
                </div>
            )}
        </div>
    );
}

function BigBtn({ children, onClick, disabled, bg, color, border, iconEl }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            style={{
                margin: 0,
                padding: '9px',
                background: disabled ? 'var(--bgTertiary)' : bg,
                color: disabled ? 'var(--textMuted)' : color,
                fontWeight: 800,
                fontSize: '12px',
                border: border ? `1px solid ${disabled ? 'var(--borderSecondary)' : border}` : 'none',
                borderRadius: '8px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
            }}
        >
            {iconEl} {children}
        </button>
    );
}

function OutlineBtn({ children, onClick, disabled, color }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            style={{
                margin: 0,
                padding: '9px',
                background: 'transparent',
                color: disabled ? 'var(--textMuted)' : color,
                fontWeight: 700,
                fontSize: '12px',
                border: `2px solid ${disabled ? 'var(--borderSecondary)' : color}`,
                borderRadius: '8px',
                cursor: disabled ? 'not-allowed' : 'pointer',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            }}
        >
            {children}
        </button>
    );
}

export default React.memo(FlightControlPanel);
